import React, { useRef, useState } from 'react';
import { ScrollView, View, Text } from 'react-native';
import {
    ActivityIndicator,
    Button,
    CustomKeyboardAvoidingView,
    Hr,
    InfoBox,
    InputWithIconButton
} from 'LocalGluons';
import { func, shape, string } from 'prop-types';
import { t } from 'Locale';
import theme from 'Theme';
import { useStore } from 'Store';

import defaultStyles from './PageStyles.jss';

const NewPassword = ({ onSaved, data, navigation }) => {
    const { appManager } = useStore();
    const [password, setPassword] = useState('');
    const [passwordRepeat, setPasswordRepeat] = useState('');
    const [errorMessage, setErrorMessage] = useState(null);
    const [secureIconPressed, setSecureIconPressed] = useState(false);
    const [loading, setLoading] = useState(false);
    const currentPasswordRef = useRef({});

    const onSecureIconPressed = (state) => {
        setSecureIconPressed(state);
    };

    const close = () => {
        navigation.pop();
    };

    const savePassword = async () => {
        if (password.trim().length < 6) {
            setErrorMessage(t('password_short'));
            return;
        }
        if (password !== passwordRepeat) {
            setErrorMessage(t('COMMON.PASSWORD_MUST_MATCH'));
            return;
        }

        setLoading(true);
        try {
            const {
                backstageConnector: { auth, events }
            } = appManager.getApi();
            const { loginOrNickname, code } = data;
            await auth.resetNewPassword({
                loginOrNickname,
                code,
                password
            });
            events.blogs.logEvent({ time: Date.now(), type: 'general', subtype: 'resetPassword' });
            onSaved();
        } catch (error) {
            setErrorMessage(error.localizedMessage);
        } finally {
            setLoading(false);
        }
    };

    const onCurrentPasswordSubmit = () => {
        currentPasswordRef.current.focus();
    };

    return (
        <CustomKeyboardAvoidingView>
            <View style={{ flex: 1 }}>
                <ScrollView contentContainerStyle={defaultStyles.container}>
                    <Text style={defaultStyles.title}>{t('SETTINGS.SET_NEW_PASSWORD')}</Text>
                    <Hr />
                    {!!errorMessage && (
                        <InfoBox style={defaultStyles.infoBox} message={errorMessage} />
                    )}
                    <Text style={defaultStyles.description} numberOfLines={4}>
                        {t('SETTINGS.SET_NEW_PASSWORD_MSG')}
                    </Text>
                    <InputWithIconButton
                        secureTextEntry
                        style={[defaultStyles.input, { marginBottom: 0 }]}
                        placeholder={t('COMMON.PASSWORD')}
                        placeholderTextColor={theme.placeholderTextColor}
                        onChangeText={setPassword}
                        value={password}
                        onSecureIconPress={onSecureIconPressed}
                        returnKeyType="next"
                        onSubmitEditing={onCurrentPasswordSubmit}
                    />
                    <InputWithIconButton
                        setRef={(ref) => {
                            currentPasswordRef.current = ref;
                        }}
                        secureTextEntry
                        style={[defaultStyles.input, { marginBottom: 0 }]}
                        placeholder={t('COMMON.PASSWORD_AGAIN')}
                        placeholderTextColor={theme.placeholderTextColor}
                        onChangeText={setPasswordRepeat}
                        value={passwordRepeat}
                        showSecureIcon={false}
                        showPassword={secureIconPressed}
                        returnKeyType="done"
                    />
                    <Text style={defaultStyles.grayText} numberOfLines={5}>
                        {t('SETTINGS.PASSWORD_REQUISITES')}
                    </Text>
                    <Button
                        style={defaultStyles.button}
                        title={t('SETTINGS.SAVE')}
                        onPress={savePassword}
                    />
                    <View style={defaultStyles.buttonSpacing}>
                        <Button
                            onPress={close}
                            style={defaultStyles.button}
                            title={t('PARTNER.VOYO.CLOSE_BTN')}
                            isSecondary
                        />
                    </View>
                </ScrollView>
            </View>
            {loading && <ActivityIndicator isOverlay />}
        </CustomKeyboardAvoidingView>
    );
};

NewPassword.propTypes = {
    data: shape({ login: string, passwordHash: string }).isRequired,
    onSaved: func.isRequired
};

export default NewPassword;
